import { DateTime } from "luxon";
import { isRecurringEvent } from "./slotOccurrences";

export const getEventTimezone = (eventOrTimezone) => {
  if (Array.isArray(eventOrTimezone?.timezone) && eventOrTimezone.timezone[0]) {
    return eventOrTimezone.timezone[0];
  }
  if (typeof eventOrTimezone?.timezone === "string" && eventOrTimezone.timezone) {
    return eventOrTimezone.timezone;
  }
  if (typeof eventOrTimezone === "string" && eventOrTimezone) return eventOrTimezone;
  return "America/New_York";
};

export const toEventWallDateTime = (value, eventOrTimezone) => {
  if (!value) return null;

  const zone = getEventTimezone(eventOrTimezone);
  const dateTime = value instanceof Date
    ? DateTime.fromJSDate(value).setZone(zone)
    : DateTime.fromISO(value, { zone });

  if (!dateTime.isValid) return null;
  return dateTime;
};

export const toCalendarWallDate = (value, eventOrTimezone) => {
  const dateTime = toEventWallDateTime(value, eventOrTimezone);
  if (!dateTime) {
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  return new Date(
    dateTime.year,
    dateTime.month - 1,
    dateTime.day,
    dateTime.hour,
    dateTime.minute,
    dateTime.second,
    dateTime.millisecond
  );
};

export const formatEventDate = (value, eventOrTimezone, format = "MMM d") =>
  toEventWallDateTime(value, eventOrTimezone)?.toFormat(format) || "";

export const formatEventTime = (value, eventOrTimezone, format = "h:mm a") =>
  toEventWallDateTime(value, eventOrTimezone)?.toFormat(format) || "";

/** Recurrence occurrence keys are wall dates — do not timezone-convert for labels. */
export const toOccurrenceWallDateTime = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return DateTime.utc(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );
};

export const formatOccurrenceWallDate = (value, format = "MMM d") =>
  toOccurrenceWallDateTime(value)?.toFormat(format) || "";

export const toOccurrenceDateTime = (value, event) =>
  isRecurringEvent(event)
    ? toOccurrenceWallDateTime(value)
    : toEventWallDateTime(value, event);

export const formatOccurrenceDate = (value, event, format = "MMM d") =>
  toOccurrenceDateTime(value, event)?.toFormat(format) || "";

const toUTCDateParts = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
    second: date.getUTCSeconds(),
    millisecond: date.getUTCMilliseconds(),
  };
};

const toZonedDateParts = (value, eventOrTimezone) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const dateTime = DateTime.fromJSDate(date, { zone: getEventTimezone(eventOrTimezone) });
  if (!dateTime.isValid) return null;
  return {
    year: dateTime.year,
    month: dateTime.month,
    day: dateTime.day,
  };
};

const toSlotOccurrenceDateTime = (slotDateValue, occurrenceStart, event) => {
  if (!slotDateValue || !occurrenceStart) return null;

  const occurrenceWall = isRecurringEvent(event)
    ? toOccurrenceWallDateTime(occurrenceStart)
    : toEventWallDateTime(occurrenceStart, event);
  if (!occurrenceWall?.isValid) return null;

  const slotParts = toUTCDateParts(slotDateValue);
  if (!slotParts) return null;

  const slotDateParts = toUTCDateParts(slotDateValue);
  const templateParts = toZonedDateParts(event?.date?.start || occurrenceStart, event);
  if (!slotDateParts || !templateParts) return null;

  const startOfLocalDay = (parts) =>
    DateTime.local(parts.year, parts.month, parts.day).startOf("day");

  const dayOffsetDays = Math.round(
    startOfLocalDay(slotDateParts).diff(startOfLocalDay(templateParts), "days").days
  );

  return occurrenceWall
    .set({
      hour: slotParts.hour,
      minute: slotParts.minute,
      second: slotParts.second,
      millisecond: slotParts.millisecond,
    })
    .plus({ days: dayOffsetDays });
};

/** Compose a slot's stored wall-clock time onto an occurrence date. */
export const getSlotOccurrenceDateTime = (slotDateValue, occurrenceStart, event) => {
  const dt = toSlotOccurrenceDateTime(slotDateValue, occurrenceStart, event);
  return dt?.isValid ? dt.toJSDate() : null;
};

export const formatSlotOccurrenceDate = (
  slotDateValue,
  occurrenceStart,
  event,
  format = "MMM d"
) => {
  const dt = toSlotOccurrenceDateTime(slotDateValue, occurrenceStart, event);
  return dt?.isValid ? dt.toFormat(format) : "";
};

export const formatSlotOccurrenceTime = (
  slotDateValue,
  occurrenceStart,
  event,
  format = "h:mm a"
) => {
  const dt = toSlotOccurrenceDateTime(slotDateValue, occurrenceStart, event);
  return dt?.isValid ? dt.toFormat(format) : "";
};

export const formatSlotOccurrenceTimeRange = (
  slot,
  occurrenceStart,
  event,
  format = "h:mm a"
) => {
  if (!slot?.start) return "";
  const start = formatSlotOccurrenceTime(slot.start, occurrenceStart, event, format);
  const end = slot.end
    ? formatSlotOccurrenceTime(slot.end, occurrenceStart, event, format)
    : "";
  if (start && end) return `${start} – ${end}`;
  return start;
};
